import type {
  CapabilityCatalogs,
  CapabilityChip,
  CapabilitySelection,
  IndustrialType,
  SelectOption,
  SelectOptionGroup,
  SlugData,
} from '@indutrust/shared-model';
import {
  mergeCapabilitySelections,
  normalizeCapabilitySelection,
  resolveCapabilityChips,
} from '@indutrust/shared-util';

/** Maps resolved capability chips to flat select options (leaf id + label). */
export function mapCapabilityChipsToSelectOptions(
  chips: readonly CapabilityChip[]
): SelectOption[] {
  return chips.map((chip) => ({
    label: `${chip.parentName} — ${chip.leafName}`,
    value: chip.id,
  }));
}

/** Maps resolved capability chips to parent-grouped select options. */
export function mapCapabilityChipsToSelectOptionGroups(
  chips: readonly CapabilityChip[]
): SelectOptionGroup[] {
  const groups = new Map<string, SelectOption[]>();

  for (const chip of chips) {
    const options = groups.get(chip.parentName) ?? [];
    if (!options.some((option) => option.value === chip.id)) {
      options.push({ label: chip.leafName, value: chip.id });
    }
    groups.set(chip.parentName, options);
  }

  return [...groups.entries()].map(([label, options]) => ({ label, options }));
}

/** Builds select options from a capability leaf selection and catalogs. */
export function mapCapabilitySelectionToSelectOptions(
  selection: CapabilitySelection,
  catalogs: CapabilityCatalogs
): SelectOption[] {
  return mapCapabilityChipsToSelectOptions(
    resolveCapabilityChips(selection, catalogs)
  );
}

/** Builds grouped select options from a capability leaf selection and catalogs. */
export function mapCapabilitySelectionToSelectOptionGroups(
  selection: CapabilitySelection,
  catalogs: CapabilityCatalogs
): SelectOptionGroup[] {
  return mapCapabilityChipsToSelectOptionGroups(
    resolveCapabilityChips(selection, catalogs)
  );
}

/** Merges capability selections and resolves grouped chips. */
export function resolveAggregatedCapabilityChips(
  selections: readonly CapabilitySelection[],
  catalogs: CapabilityCatalogs
): CapabilityChip[] {
  return resolveCapabilityChips(
    mergeCapabilitySelections(...selections),
    catalogs
  );
}

/** Resolves industrial subtype ids to display tags. */
export function resolveIndustrialSubtypeTags(
  subtypeIds: readonly number[],
  industrialTypes: readonly IndustrialType[]
): SlugData[] {
  const tags: SlugData[] = [];
  const seen = new Set<number>();

  for (const type of industrialTypes) {
    for (const subtype of type.industrial_subtypes) {
      if (subtypeIds.includes(subtype.id) && !seen.has(subtype.id)) {
        tags.push(subtype);
        seen.add(subtype.id);
      }
    }
  }

  return tags;
}

/** Collects unique industrial subtype ids from project references. */
export function collectIndustrialSubtypeIds(
  references: readonly { industrial_subtype_ids: readonly number[] }[]
): number[] {
  const ids = new Set<number>();
  for (const reference of references) {
    for (const id of reference.industrial_subtype_ids) {
      ids.add(id);
    }
  }
  return [...ids];
}

/** Returns true when any selection declares at least one capability leaf. */
export function hasAnyCapabilityLeaves(
  ...selections: readonly (Partial<CapabilitySelection> | null | undefined)[]
): boolean {
  return selections.some(
    (selection) =>
      normalizeCapabilitySelection(selection).capability_ids.length > 0
  );
}
